// Rasterise an on-screen <svg> (with remote <image> hrefs inlined) to a PNG blob.
const toDataUrl = async (url) => {
  const res = await fetch(url, { credentials: "include" });
  const blob = await res.blob();
  return new Promise((ok, no) => {
    const r = new FileReader();
    r.onload = () => ok(r.result);
    r.onerror = no;
    r.readAsDataURL(blob);
  });
};

export const svgToPngBlob = async (svgEl, { scale = 3, background = "#09090b" } = {}) => {
  const clone = svgEl.cloneNode(true);
  const imgs = [...clone.querySelectorAll("image")];
  await Promise.all(
    imgs.map(async (im) => {
      const href = im.getAttribute("href") || im.getAttribute("xlink:href");
      if (!href || href.startsWith("data:")) return;
      try {
        im.setAttribute("href", await toDataUrl(href));
      } catch {
        im.remove();
      }
    }),
  );
  const rect = svgEl.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width));
  const h = Math.max(1, Math.round(rect.height));
  clone.setAttribute("width", w);
  clone.setAttribute("height", h);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const src = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([src], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const img = await new Promise((ok, no) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = no;
      i.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise((ok) => canvas.toBlob(ok, "image/png"));
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Clipboard first, download as fallback. Returns "copied" | "downloaded".
export const copyOrDownloadPng = async (blob, filename) => {
  try {
    if (navigator.clipboard?.write && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      return "copied";
    }
  } catch {
    /* fall through to download */
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  return "downloaded";
};
